import { FALLBACK_FILENAME, MIME_MP4 } from './const';

// A user-cancelled share must NOT fall back to a plain download, so the caller needs
// to tell the outcomes apart.
export type TShareOutcome = 'shared' | 'unsupported' | 'aborted' | 'error';

// Injected browser capabilities — keeps shareVideo() testable without navigator/fetch
// (mirrors the IHttpClient injection in ai-text-lib). The real adapter lives in
// browser.ts; tests pass a plain fake.
export interface IShareCapabilities {
    canShareFiles(files: File[]): boolean;
    share(files: File[]): Promise<void>;
    fetchFile(
        url: string,
        name: string,
        onProgress?: (ratio: number | null) => void
    ): Promise<File>;
}

// The slice of a stream reader we depend on — lets collectStream() be unit-tested.
export interface IByteReader {
    read(): Promise<{ done: boolean; value?: Uint8Array }>;
}

// The navigator bits isIos() reads (a real Navigator satisfies it; tests pass a fake).
export interface IUaInfo {
    userAgent: string;
    platform: string;
    maxTouchPoints: number;
}

/**
 * The share enhancement targets iOS only: elsewhere (Android Chrome, desktop) a plain
 * link with Content-Disposition: attachment already downloads cleanly, so hijacking it
 * into a fetch-then-share would only regress a working flow. iPadOS 13+ reports as
 * "MacIntel" with a touch screen, so catch that alongside the classic iP* UAs.
 */
export function isIos(ua: IUaInfo): boolean {
    return (
        /iPad|iPhone|iPod/.test(ua.userAgent) ||
        (ua.platform === 'MacIntel' && ua.maxTouchPoints > 1)
    );
}

// Derive a sane *.mp4 filename from the URL when the caller didn't supply one.
export function fileNameFromUrl(url: string, fallback = FALLBACK_FILENAME): string {
    try {
        const path = url.split(/[?#]/)[0];
        const base = decodeURIComponent(path.slice(path.lastIndexOf('/') + 1));
        return base && /\.mp4$/i.test(base) ? base : fallback;
    } catch {
        return fallback;
    }
}

/**
 * Drain a byte stream into chunks, reporting download progress against a known total
 * (ratio in [0..1]). A FullHD clip runs to tens of MB, hence the progress feedback.
 * total <= 0 means the size is unknown → progress is indeterminate (one null tick).
 */
export async function collectStream(
    reader: IByteReader,
    total: number,
    onProgress?: (ratio: number | null) => void
): Promise<Uint8Array[]> {
    if (total <= 0) {
        onProgress?.(null);
    }
    const chunks: Uint8Array[] = [];
    let received = 0;
    for (;;) {
        const { done, value } = await reader.read();
        if (done) {
            break;
        }
        if (value) {
            chunks.push(value);
            received += value.length;
            if (total > 0) {
                onProgress?.(Math.min(1, received / total));
            }
        }
    }
    return chunks;
}

// The slice of a fetch Response responseToFile() depends on — a real Response is
// structurally assignable, and a plain fake satisfies it in tests (jsdom has no
// Response global).
export interface IFetchResponse {
    ok: boolean;
    status: number;
    headers: { get(name: string): string | null };
    body: { getReader(): IByteReader } | null;
    blob(): Promise<Blob>;
}

/**
 * Turn a fetched response into an mp4 File, streaming for progress when the length is
 * known (else a plain blob() with one indeterminate tick). Split out from the fetch
 * call in browser.ts so the Content-Length branching is unit-testable.
 */
export async function responseToFile(
    res: IFetchResponse,
    name: string,
    onProgress?: (ratio: number | null) => void
): Promise<File> {
    if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
    }

    const total = Number(res.headers.get('Content-Length')) || 0;
    if (!res.body || !total) {
        onProgress?.(null);
        return new File([await res.blob()], name, { type: MIME_MP4 });
    }

    const chunks = await collectStream(res.body.getReader(), total, onProgress);
    return new File(chunks, name, { type: MIME_MP4 });
}

// A dummy File to probe files-share support without materialising the real mp4.
// Shared by shareVideo() and browser.ts::supportsFileShare().
export function probeFile(): File {
    return new File([new Blob()], 'probe.mp4', { type: MIME_MP4 });
}

/**
 * Orchestrate probe → fetch → native share sheet. Returns an outcome the caller uses
 * to decide UX: only 'unsupported'/'error' should fall back to a plain download.
 */
export async function shareVideo(
    url: string,
    name: string,
    cap: IShareCapabilities,
    onProgress?: (ratio: number | null) => void
): Promise<TShareOutcome> {
    if (!cap.canShareFiles([probeFile()])) {
        return 'unsupported';
    }

    let file: File;
    try {
        file = await cap.fetchFile(url, name, onProgress);
    } catch {
        return 'error';
    }

    if (!cap.canShareFiles([file])) {
        return 'unsupported';
    }

    try {
        await cap.share([file]);
        return 'shared';
    } catch (e) {
        // AbortError => the user dismissed the sheet: not a failure, don't fall back.
        if (e instanceof DOMException && e.name === 'AbortError') {
            return 'aborted';
        }
        // NotAllowedError et al. → 'error'. share() needs transient activation
        // (~5s); a slow fetch of a large FullHD clip can outlast it. The caller then
        // falls back to the plain download URL — still an attachment download (iOS
        // 13+ saves to Files), so the worst case beats today's "opens in the player".
        return 'error';
    }
}
